"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DatabaseDiff = exports.TypeDiff = exports.EntityDiff = exports.AttributeDiff = exports.ExtraDiff = exports.OptValueDiff = exports.ValueDiff = exports.ArrayDiff = void 0;
exports.databaseDiff = databaseDiff;
exports.attributesDiff = attributesDiff;
const zod_1 = require("zod");
const utils_1 = require("@azimutt/utils");
const database_1 = require("./database");
const databaseUtils_1 = require("./databaseUtils");
// FIXME: Work In Progress
// cf https://github.com/andreyvit/json-diff
const ArrayDiff = (schema, schemaDiff) => zod_1.z.object({
    unchanged: schema.and(zod_1.z.object({ i: zod_1.z.number() })).array(), // both present but same
    updated: schemaDiff.array(), // both present but different
    created: schema.and(zod_1.z.object({ i: zod_1.z.number() })).array(), // after only
    deleted: schema.and(zod_1.z.object({ i: zod_1.z.number() })).array(), // before only
}).strict();
exports.ArrayDiff = ArrayDiff;
const ValueDiff = (schema) => zod_1.z.object({ before: schema, after: schema }).strict();
exports.ValueDiff = ValueDiff;
const OptValueDiff = (schema) => zod_1.z.object({ before: schema.optional(), after: schema.optional() }).strict();
exports.OptValueDiff = OptValueDiff;
exports.ExtraDiff = zod_1.z.record(zod_1.z.object({ before: zod_1.z.any(), after: zod_1.z.any() }));
exports.AttributeDiff = zod_1.z.object({
    name: database_1.AttributeName,
    i: (0, exports.ValueDiff)(zod_1.z.number()).optional(),
    type: (0, exports.ValueDiff)(database_1.AttributeType).optional(),
    null: (0, exports.OptValueDiff)(zod_1.z.boolean()).optional(),
    // gen: z.boolean().optional(), // false when not specified
    default: (0, exports.OptValueDiff)(database_1.AttributeValue).optional(),
    // attrs: z.lazy(() => Attribute.array().optional()),
    doc: (0, exports.OptValueDiff)(zod_1.z.string()).optional(),
    // stats: AttributeStats.optional(),
    extra: exports.ExtraDiff.optional(),
}).strict();
exports.EntityDiff = database_1.Namespace.extend({
    name: database_1.EntityName,
    kind: (0, exports.OptValueDiff)(database_1.EntityKind).optional(),
    def: (0, exports.OptValueDiff)(zod_1.z.string()).optional(),
    attrs: (0, exports.ArrayDiff)(database_1.Attribute, exports.AttributeDiff).optional(),
    pk: (0, exports.OptValueDiff)(database_1.PrimaryKey).optional(),
    // TODO: indexes: Index.array().optional(),
    // TODO: checks: Check.array().optional(),
    doc: (0, exports.OptValueDiff)(zod_1.z.string()).optional(),
    // stats: EntityStats.optional(),
    extra: exports.ExtraDiff.optional(),
}).strict();
exports.TypeDiff = database_1.Namespace.extend({
    name: database_1.TypeName,
    alias: (0, exports.OptValueDiff)(zod_1.z.string()).optional(),
    values: (0, exports.OptValueDiff)(zod_1.z.string().array()).optional(),
    attrs: (0, exports.ArrayDiff)(database_1.Attribute, exports.AttributeDiff).optional(),
    definition: (0, exports.OptValueDiff)(zod_1.z.string()).optional(),
    doc: (0, exports.OptValueDiff)(zod_1.z.string()).optional(),
    extra: exports.ExtraDiff.optional(),
}).strict().describe('TypeDiff');
exports.DatabaseDiff = zod_1.z.object({
    entities: (0, exports.ArrayDiff)(database_1.Entity, exports.EntityDiff).optional(),
    // TODO: relations: ArrayDiff(Relation, RelationDiff).optional(),
    types: (0, exports.ArrayDiff)(database_1.Type, exports.TypeDiff).optional(),
}).strict().describe('DatabaseDiff');
function databaseDiff(before, after) {
    const entities = arrayDiffBy(before.entities || [], after.entities || [], databaseUtils_1.entityToId, entityDiff);
    const types = arrayDiffBy(before.types || [], after.types || [], databaseUtils_1.typeToId, typeDiff);
    return (0, utils_1.removeEmpty)({ entities, types });
}
function entityDiff(before, after) {
    const kind = before.kind === undefined && after.kind === undefined ? undefined : valueDiff(before.kind, after.kind);
    const def = before.def === after.def ? undefined : valueDiff(before.def, after.def);
    const attrs = attributesDiff(before.attrs || [], after.attrs || []);
    const pk = (0, utils_1.equalDeep)(before.pk, after.pk) ? undefined : valueDiff(before.pk, after.pk);
    const doc = before.doc === after.doc ? undefined : valueDiff(before.doc, after.doc);
    const extra = extraDiff(before.extra || {}, after.extra || {});
    if ([kind, def, attrs, pk, doc, extra].every(v => v === undefined))
        return undefined;
    return (0, utils_1.removeUndefined)({ ...(0, databaseUtils_1.typeToNamespace)(before), name: before.name, kind, def, attrs, pk, doc, extra });
}
function typeDiff(before, after) {
    const alias = before.alias === after.alias ? undefined : valueDiff(before.alias, after.alias);
    const values = (0, utils_1.arraySame)(before.values || [], after.values || [], (a, b) => a === b) ? undefined : valueDiff(before.values, after.values);
    const attrs = attributesDiff(before.attrs || [], after.attrs || []);
    const definition = before.definition === after.definition ? undefined : valueDiff(before.definition, after.definition);
    const doc = before.doc === after.doc ? undefined : valueDiff(before.doc, after.doc);
    const extra = extraDiff(before.extra || {}, after.extra || {});
    if ([alias, values, attrs, definition, doc, extra].every(v => v === undefined))
        return undefined;
    return (0, utils_1.removeUndefined)({ ...(0, databaseUtils_1.typeToNamespace)(before), name: before.name, alias, values, attrs, definition, doc, extra });
}
function attributesDiff(before, after) {
    return arrayDiffBy(before, after, a => a.name, attributeDiff);
}
function attributeDiff(before, after) {
    const i = before.i === after.i ? undefined : valueDiff(before.i, after.i);
    const type = before.type === after.type ? undefined : valueDiff(before.type, after.type);
    const nullable = before.null === after.null ? undefined : valueDiff(before.null, after.null);
    const defaultValue = before.default === after.default ? undefined : valueDiff(before.default, after.default);
    const doc = before.doc === after.doc ? undefined : valueDiff(before.doc, after.doc);
    const extra = extraDiff(before.extra || {}, after.extra || {});
    if ([i, type, nullable, defaultValue, doc, extra].every(v => v === undefined))
        return undefined;
    return (0, utils_1.removeUndefined)({ ...(0, databaseUtils_1.typeToNamespace)(before), name: before.name, i, type, null: nullable, default: defaultValue, doc, extra });
}
function extraDiff(before, after) {
    const res = {};
    Object.entries(before).forEach(([key, value]) => {
        if (!(0, utils_1.anySame)(value, after[key])) {
            res[key] = { before: value, after: after[key] };
        }
    });
    Object.entries(after).forEach(([key, value]) => {
        if (!(key in before)) {
            res[key] = { before: undefined, after: value };
        }
    });
    return Object.keys(res).length === 0 ? undefined : res;
}
// generic functions
function arrayDiffBy(leftArr, rightArr, getKey, makeDiff) {
    const { left: deleted, right: created, both: common } = (0, utils_1.diffBy)(leftArr.map((v, i) => Object.assign({ i }, v)), rightArr.map((v, i) => Object.assign({ i }, v)), getKey);
    const updated = [];
    const unchanged = [];
    common.forEach(({ left, right }) => {
        const res = makeDiff(left, right);
        res === undefined ? unchanged.push(left) : updated.push(res);
    });
    return unchanged.length > 0 || updated.length > 0 || created.length > 0 || deleted.length > 0 ? (0, utils_1.removeEmpty)({ unchanged, updated, created, deleted }) : undefined;
}
function valueDiff(before, after) {
    return { before, after };
}
//# sourceMappingURL=databaseDiff.js.map